import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { LOGO_URL } from '../utils/seo';

const ACCOUNT_REQUEST_ENDPOINT = '/api/account-aanvragen.php';

const initialFormData = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  kvkNumber: '',
  streetAddress: '',
  postalCode: '',
  city: '',
  message: '',
  website: '',
};

const AccountAanvragen = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [submitState, setSubmitState] = useState({
    isSubmitting: false,
    status: '',
    message: '',
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitState({
      isSubmitting: true,
      status: '',
      message: '',
    });

    try {
      const response = await fetch(ACCOUNT_REQUEST_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'De aanvraag kon niet worden verzonden.');
      }

      setFormData(initialFormData);
      setSubmitState({
        isSubmitting: false,
        status: 'success',
        message: result.message || 'Uw aanvraag is verzonden.',
      });
    } catch (error) {
      setSubmitState({
        isSubmitting: false,
        status: 'error',
        message: error.message || 'De aanvraag kon niet worden verzonden.',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Account aanvragen voor de webshop - Timmers BV</title>
        <meta name="description" content="Vraag een account aan voor de Timmers BV webshop. Na controle voegen wij uw bedrijf handmatig toe in Baka." />
        <link rel="canonical" href="https://timmers-bv.nl/account-aanvragen" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Account aanvragen voor de webshop - Timmers BV" />
        <meta property="og:description" content="Vraag een account aan voor de Timmers BV webshop. Wij controleren de aanvraag voordat het account in Baka wordt toegevoegd." />
        <meta property="og:image" content={LOGO_URL} />
        <meta property="og:url" content="https://timmers-bv.nl/account-aanvragen" />
        <meta property="og:locale" content="nl_NL" />
      </Helmet>

      <main id="maincontent" className="page-main account-request-page">
        <div className="row-full-width-inner">
          <div className="account-request-layout">
            <section className="account-request-intro">
              <div className="sub-text">webshop account</div>
              <h1>Account aanvragen</h1>
              <p>
                Vul het registratieformulier in om toegang tot de Timmers BV webshop aan te vragen.
                De aanvraag moet eerst handmatig worden gecontroleerd voordat het account in Baka wordt toegevoegd.
                Na goedkeuring ontvangt u een e-mail met verdere instructies.
              </p>
            </section>

            <section className="account-request-form-card" aria-labelledby="account-request-form-title">
              <h2 id="account-request-form-title">Registratieformulier</h2>
              <form className="account-request-form" onSubmit={handleSubmit}>
                <label className="account-request-honeypot" aria-hidden="true">
                  Website
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </label>
                <div className="account-request-grid">
                  <label>
                    Bedrijfsnaam *
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      autoComplete="organization"
                    />
                  </label>
                  <label>
                    Contactpersoon *
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                    />
                  </label>
                  <label>
                    E-mailadres *
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                    />
                  </label>
                  <label>
                    Telefoonnummer *
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                    />
                  </label>
                  <label>
                    KvK-nummer
                    <input
                      type="text"
                      name="kvkNumber"
                      value={formData.kvkNumber}
                      onChange={handleChange}
                      inputMode="numeric"
                    />
                  </label>
                  <label>
                    Straat
                    <input
                      type="text"
                      name="streetAddress"
                      value={formData.streetAddress}
                      onChange={handleChange}
                      autoComplete="street-address"
                    />
                  </label>
                  <label>
                    Postcode
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      autoComplete="postal-code"
                    />
                  </label>
                  <label>
                    Plaats
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      autoComplete="address-level2"
                    />
                  </label>
                </div>

                {submitState.message ? (
                  <div className={`account-request-message ${submitState.status}`} role="status">
                    {submitState.message}
                  </div>
                ) : null}

                <button type="submit" className="button" disabled={submitState.isSubmitting}>
                  {submitState.isSubmitting ? 'Aanvraag versturen...' : 'Aanvraag versturen'}
                </button>
              </form>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default AccountAanvragen;
